import { containsIgnoreCase } from "../../utils/stringUtils";

export const ACTIVE_CHECK_STATES = new Set(["queued", "processing", "retrying"]);

export const isCheckActive = (singleCase) =>
  ACTIVE_CHECK_STATES.has(singleCase?.check_status?.state);

export const sortCasesNewestFirst = (caseList = []) =>
  [...caseList].sort((a, b) => {
    const aDate = Date.parse(a?.created_at || "");
    const bDate = Date.parse(b?.created_at || "");
    const aTimestamp = Number.isNaN(aDate) ? 0 : aDate;
    const bTimestamp = Number.isNaN(bDate) ? 0 : bDate;

    return bTimestamp - aTimestamp;
  });

export const matchesSearch = (singleCase, search) =>
  !search ||
  containsIgnoreCase(singleCase.court, search) ||
  containsIgnoreCase(singleCase.note, search) ||
  containsIgnoreCase(String(singleCase.gak || ""), search);

// URL filter values can be missing, empty, "all" or the literal "null"
// (written by the classic handlers); all of them mean "no filter".
const isUnset = (value) =>
  value === null || value === undefined || value === "" || value === "null" || value === "all";

export const applyCaseFilters = (
  cases,
  { decisions, syzitisi, diadikasia, court, epan } = {}
) =>
  cases.filter((singleCase) => {
    if (!isUnset(decisions)) {
      const hasDecision = Boolean(singleCase.arithmos_apofasis?.includes("/"));
      if (decisions === "yes" && !hasDecision) return false;
      if (decisions === "no" && hasDecision) return false;
    }
    if (!isUnset(syzitisi)) {
      const discussed = Boolean(
        singleCase.apotelesma?.toLowerCase().includes("συζητ")
      );
      if (syzitisi === "yes" && !discussed) return false;
      if (syzitisi === "no" && discussed) return false;
    }
    if (!isUnset(diadikasia) && !singleCase.diadikasia?.includes(diadikasia)) {
      return false;
    }
    if (!isUnset(court) && !singleCase.court?.includes(court)) {
      return false;
    }
    if (!isUnset(epan)) {
      // Ν. 5221/2025 re-scheduling: pending | filed | application
      const status = singleCase.epanaprosdiorismos;
      const matches =
        epan === "application"
          ? status?.role === "application"
          : status?.role === "old" && status.status === epan;
      if (!matches) return false;
    }
    return true;
  });

// Classic sort keys: `${field}_${direction}`, field in created|court|last|fav.
export const sortCases = (cases, sortBy = "created_desc") => {
  const sorted = [...cases];
  const [field, direction] = sortBy.split("_");
  const isAsc = direction === "asc";

  sorted.sort((a, b) => {
    let compare = 0;

    if (field === "created") {
      compare = (a.created_at || "").localeCompare(b.created_at || "");
    } else if (field === "court") {
      compare = (a.court || "").localeCompare(b.court || "");
    } else if (field === "last") {
      compare = (a.last_checked || "").localeCompare(b.last_checked || "");
    } else if (field === "fav") {
      compare = (a.fav || 0) - (b.fav || 0);
    }

    return isAsc ? compare : -compare;
  });

  return sorted;
};

// Extracts the hearing date from arithmos_pinakiou (the last dd/mm/yyyy in it,
// like extractLastDate in DashboardDikasimoi) as a sortable yyyymmdd number.
export const parseDikasimos = (arithmosPinakiou) => {
  if (!arithmosPinakiou) return null;
  const matches = String(arithmosPinakiou).match(/\d{2}\/\d{2}\/\d{4}/g);
  if (!matches) return null;
  const [day, month, year] = matches[matches.length - 1].split("/");
  return Number(year) * 10000 + Number(month) * 100 + Number(day);
};

// Solon recorded a re-scheduling (Ν. 5221/2025): the listed date no longer stands.
export const isDikasimosRescheduled = (singleCase) =>
  singleCase?.epanaprosdiorismos?.status === "filed" &&
  !singleCase.epanaprosdiorismos.filed_by_link;

export const formatYmd = (ymd) => {
  if (!ymd) return "";
  const year = Math.floor(ymd / 10000);
  const month = Math.floor((ymd % 10000) / 100);
  const day = ymd % 100;
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
};

export const getSelectedDiadikasia = (singleCase) => {
  const entry = singleCase?.userInput?.find(
    (e) => e.category === "select_diadikasia"
  );
  if (!entry) return "";
  try {
    return JSON.parse(entry.data);
  } catch {
    return "";
  }
};

const compareValues = (a, b) => {
  const aEmpty = a === null || a === undefined || a === "";
  const bEmpty = b === null || b === undefined || b === "";
  if (aEmpty && bEmpty) return 0;
  // Empty values always sort last, regardless of direction.
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "el", { numeric: true });
};

// Sorts by a column's sortValue(); ties fall back to newest-first.
export const sortByColumn = (cases, column, dir) => {
  if (!column?.sortValue || !dir) return cases;
  const factor = dir === "asc" ? 1 : -1;
  const withIndex = cases.map((c, index) => ({ c, index, v: column.sortValue(c) }));
  withIndex.sort((x, y) => {
    const xEmpty = x.v === null || x.v === undefined || x.v === "";
    const yEmpty = y.v === null || y.v === undefined || y.v === "";
    if (xEmpty !== yEmpty) return xEmpty ? 1 : -1;
    const cmp = compareValues(x.v, y.v) * factor;
    return cmp !== 0 ? cmp : x.index - y.index;
  });
  return withIndex.map((x) => x.c);
};
